/**
 * Gate I8.13: Shared Timeline Utilities
 *
 * Extracted from animal-detail.tsx to enable consistent formatting
 * of timeline changes across the Animal Detail page and Shared Animal Drawer.
 *
 * Gate S8.1 Step 1: Added LOCATION_LABELS and buildStatusSummary (moved from
 * AnimalTimelineCard.tsx to make them available to the drawer's Current Stay section).
 */

export interface FieldChange {
  field: string;
  oldValue: string | null;
  newValue: string | null;
}

/**
 * Human-readable labels for shelter location codes.
 * Used by both the Stays tab (AnimalTimelineCard) and the drawer's Current Stay preview.
 */
export const LOCATION_LABELS: Record<string, string> = {
  SALEM: "Salem",
  BOSTON: "Boston",
  METHUEN: "Methuen",
  CAPE: "Cape Cod",
};

/**
 * Build a human-readable status summary sentence from a snapshot's normalised status fields.
 * E.g. "Unavailable (behavior) · Level 3"
 *
 * Used by both the Stays tab (AnimalTimelineCard) and the drawer's Current Stay preview.
 */
export function buildStatusSummary(snapshot: {
  kennelStatusNorm: string | null;
  substatusNorm: string | null;
  statusCode: string | null;
  adoptionLevelNorm: string | null;
}): string {
  const parts: string[] = [];

  if (snapshot.kennelStatusNorm) {
    const status = snapshot.kennelStatusNorm;
    if (status === "AVAILABLE") parts.push("Available");
    else if (status === "UNAVAIL") parts.push("Unavailable");
    else if (status === "STRAY_WAIT") parts.push("Stray wait");
    else if (status === "EVALUATION") parts.push("Under evaluation");
    else {
      parts.push(
        status
          .split("_")
          .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
          .join(" ")
      );
    }
  } else if (snapshot.statusCode) {
    parts.push(`Status: ${snapshot.statusCode}`);
  }

  if (snapshot.substatusNorm) {
    const substatus = snapshot.substatusNorm;
    if (substatus === "MEDICAL") parts.push("(medical)");
    else if (substatus === "BEHAVIOR") parts.push("(behavior)");
    else if (substatus === "BEH+MED") parts.push("(medical + behavior)");
    else if (substatus === "PENDADOPT") parts.push("(pending adoption)");
    else parts.push(`(${substatus.toLowerCase()})`);
  }

  if (snapshot.adoptionLevelNorm) {
    parts.push(`· ${snapshot.adoptionLevelNorm}`);
  }

  return parts.length > 0 ? parts.join(" ") : "Status not available";
}

/**
 * Map timeline change fields to behavior-team friendly labels.
 * Source: animal-detail.tsx:1034-1056
 */
export function getTimelineChangeLabel(field: string): string {
  const map: Record<string, string> = {
    location: "Location",
    zoneCode: "Kennel",
    kennelStatusNorm: "Status",
    substatusNorm: "Substatus",
    adoptionLevelNorm: "Adoption level",
    intakeDate: "Intake date",
  };

  if (map[field]) {
    return map[field];
  }

  return field
    .replace(/([A-Z])/g, " $1")
    .trim()
    .replace(/\s+/g, " ")
    .replace(/^./, (c) => c.toUpperCase());
}

/**
 * Deduplicate status changes by suppressing statusCode when kennelStatusNorm changed.
 * Source: animal-detail.tsx:1023-1032
 */
export function deduplicateStatusChanges(changes: FieldChange[]): FieldChange[] {
  const hasKennelStatusChange = changes.some(c => c.field === "kennelStatusNorm");
  
  if (hasKennelStatusChange) {
    return changes.filter(c => c.field !== "statusCode");
  }
  
  return changes;
}
